import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import {
  ProfileService,
  ProfileDetails,
  IdType,
  UpdateProfileRequest,
  DeleteEligibility,
} from '../profile.service';
import { interval, Observable, Subscription } from 'rxjs';
import moment from 'moment';
import { CoreService } from '@app/@core/services/core.service';
import { MatDialog } from '@angular/material/dialog';
import { Logger } from '@app/@core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { OtpDialogComponent } from './otp-dialog/otp-dialog.component';
import { finalize, tap } from 'rxjs/operators';

const log = new Logger('ProfileDetailsComponent');

import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { UploadFileCategory, UploadFileResponse } from 'src/types/types';
import { countries } from '@app/@core/countries';
import { ConfirmationDialogComponent } from '@app/@shared/confirmation-dialog/confirmation-dialog.component';
import { CredentialsService } from '@app/@core/services/credentials.service';
import { AnalyticsService } from '@app/ahoy-analytics/analytics.service';
import { Country } from '@app/@core/models/country.model';

export const MY_FORMATS = {
  parse: {
    dateInput: ['DD MMM, YYYY'],
  },
  display: {
    dateInput: 'DD MMM, YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
export function noSpecialCharactersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const isValid = /^[\p{L}\s]+$/u.test(control.value); // \p{L} covers all letters across languages
    return isValid ? null : { invalidCharacters: control.value ? true : false };
  };
}

/** Raw shape of `profileFormGroup.getRawValue()`. */
interface ProfileFormValue {
  firstName: string;
  lastName: string;
  birthDate: Date | string;
  idType: number;
  gender: number;
  email: string;
  phone: string;
  nationality: number;
  idNumber: string;
}

@Component({
  selector: 'app-profile-details',
  templateUrl: './profile-details.component.html',
  styleUrls: ['./profile-details.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  standalone: false,
})
export class ProfileDetailsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  profileFormGroup: FormGroup;
  profileDetails: ProfileDetails;
  countries: Country[];
  idTypes$: Observable<IdType[]>;
  idTypes: IdType[];
  countryCode = environment.countrySettings.countryCode;
  editingProfile = false;

  genderHash: Record<number, string> = {
    1: this.translateService.instant('Male'),
    2: this.translateService.instant('Female'),
  };
  genderTypes = [
    { id: 1, type: this.genderHash[1] },
    { id: 2, type: this.genderHash[2] },
  ];
  imgUrl: string;
  uploadingImg: boolean = false;
  coolOffCountdown = '';
  private countdownSub?: Subscription;
  enableEditPhone: boolean = false;
  enableEditEmail: boolean = false;
  otpFetching: boolean = false;
  savingEmailChanges: boolean = false;
  savingProfileChanges: boolean = false;
  phoneFormGroup: FormGroup;
  emailFormGroup: FormGroup;
  _selectedIdentificationType: string = this.translateService.instant(
    'Identification Number',
  );
  maxDate: Date;
  constructor(
    private formBuilder: FormBuilder,
    private profileService: ProfileService,
    private cdr: ChangeDetectorRef,
    private coreService: CoreService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private router: Router,
    private credentialsService: CredentialsService,
    private analytics: AnalyticsService,
  ) {
    this.maxDate = new Date();
    this.maxDate.setFullYear(this.maxDate.getFullYear() - 4);
  }

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.setForm();
    this.getProfileDetails();
    this.countries = countries;
    this.idTypes$ = this.profileService.getIdTypes().pipe(
      tap((types) => {
        this.idTypes = types;
      }),
    );
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.countdownSub?.unsubscribe();
  }

  private setForm() {
    this.profileFormGroup = this.formBuilder.group({
      firstName: ['', [Validators.required, noSpecialCharactersValidator()]],
      lastName: ['', [Validators.required, noSpecialCharactersValidator()]],
      birthDate: [''],
      idType: [''],
      gender: [''],
      email: ['', Validators.required],
      phone: ['', [Validators.required]],
      nationality: [''],
      idNumber: [''],
    });

    this.phoneFormGroup = this.formBuilder.group({
      phone: ['', [Validators.required]],
    });

    this.emailFormGroup = this.formBuilder.group({
      email: ['', Validators.required],
    });
  }

  getProfileDetails() {
    this.profileService
      .getProfileDetails(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res: ProfileDetails) => {
          this.profileDetails = res;
          this.imgUrl = res.imageUrl;
          this.profileFormGroup.get('firstName').setValue(res.firstName);
          this.profileFormGroup.get('lastName').setValue(res.lastName);
          this.profileFormGroup.get('birthDate').setValue(res.dateOfBirth);
          this.profileFormGroup.get('gender').setValue(res.gender);
          this.profileFormGroup.get('phone').setValue(res.phone);
          this.profileFormGroup.get('email').setValue(res.email);
          this.profileFormGroup
            .get('nationality')
            .setValue(res.nationality?.id);
          this.profileFormGroup
            .get('idType')
            .setValue(res.identification?.typeId);
          this.profileFormGroup
            .get('idNumber')
            .setValue(res.identification?.number);
          this.idTypeChanged(res.identification?.typeId);
          this.cdr.detectChanges();
          this.editingProfile = false;
          this.startCoolOffCountdown();
        },
        (err: HttpErrorResponse) => {
          this.editingProfile = false;
          this.coreService.showErrorDialog(err);
          log.error(err);
        },
      );
  }

  idTypeChanged(evt: number) {
    const selectedIdType = this.idTypes?.find(
      (type: IdType) => type.id === evt,
    );
    this._selectedIdentificationType = selectedIdType?.name;
  }

  get selectedIdentificationType() {
    const selectedIdType = this.idTypes?.find(
      (type: IdType) => type.id === this.profileFormGroup.get('idType').value,
    );
    return (
      selectedIdType?.name ||
      this.translateService.instant('Identification Number')
    );
  }

  updatePhoneNumber() {
    this.enableEditPhone = true;
  }

  editEmail() {
    this.enableEditEmail = true;
  }

  updateEmail() {
    this.savingEmailChanges = true;
    this.saveChanges(false, true);
  }

  verifyUser() {
    this.otpFetching = true;
    let phoneObj = {
      phoneNumber: this.phoneFormGroup.get('phone').value,
    };
    this.profileService
      .sendOtp(phoneObj)
      .pipe(
        finalize(() => {
          this.otpFetching = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        () => {
          const dialogRef = this.dialog.open(OtpDialogComponent, {
            width: '420px',
            data: {
              mode: 'verify-phone',
              phoneNumber: this.phoneFormGroup.get('phone').value,
            },
          });
          dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((dataFromChild) => {
              if (dataFromChild === true) {
                this.saveChanges(true, false);
              }
            });
        },
        (err: HttpErrorResponse) => {
          this.coreService.showErrorDialog(err);
          log.error(err);
        },
      );
  }

  isValid() {
    return (
      this.profileFormGroup &&
      !this.profileFormGroup.invalid &&
      this.profileFormGroup.touched &&
      this.profileFormGroup.dirty
    );
  }

  saveProfileChanges() {
    this.savingProfileChanges = true;
    this.enableEditEmail = false;
    this.enableEditPhone = true;
    this.saveChanges(true, false);
  }

  undoProfileChanges() {
    this.getProfileDetails();
  }

  /**
   *
   * @param modifyingPhone passed as true when we are changing the phone number
   * @param modifyingEmail  passed as true when we are changing the email
   */
  saveChanges(modifyingPhone?: boolean, modifyingEmail?: boolean) {
    this.profileService
      .updateProfileDetails(
        this.mapProfileModifications(
          this.profileFormGroup.getRawValue(),
          modifyingPhone,
          modifyingEmail,
        ),
      )
      .pipe(
        finalize(() => {
          this.savingEmailChanges = false;
          if (modifyingEmail) this.enableEditEmail = false;
          if (modifyingPhone) this.enableEditPhone = false;
          this.savingProfileChanges = false;
          this.profileFormGroup.markAsUntouched();
          this.cdr.detectChanges();
          this.editingProfile = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (res) => {
          this.analytics.trackEvent('profile_updated', {
            field_updated: 'profile_details',
          });
          this.successMsg(
            this.translateService.instant(
              'Your Changes Have Been Saved Successfully',
            ),
          );
          this.getProfileDetails();
          log.info(res);
        },
        (err: HttpErrorResponse) => {
          this.coreService.showErrorDialog(err);
          log.error(err);
        },
      );
  }

  mapProfileModifications(
    profile: ProfileFormValue,
    modifyingPhone?: boolean,
    modifyingEmail?: boolean,
  ): UpdateProfileRequest {
    let profileObj: UpdateProfileRequest = {
      Id: this.profileDetails.id,
      firstName: profile.firstName,
      lastName: profile.lastName,
      gender: profile.gender,
      email:
        this.emailFormGroup.get('email').value !== '' && modifyingEmail
          ? this.emailFormGroup.get('email').value
          : this.profileDetails.email,
      phone:
        profile.phone !== '' && modifyingPhone
          ? profile.phone
          : this.profileDetails.phone,
      preferredLanguageCode: 'En',
      imageUrl: '',

      nationalityId: profile.nationality,
      identification: {
        number: profile.idNumber,
        typeId: profile.idType,
      },
    };
    const dob = moment(profile.birthDate);
    if (profile.birthDate && dob.isValid()) {
      profileObj = {
        ...profileObj,
        dateOfBirth: dob.format('YYYY-MM-DDThh:mm:ss'),
        dateofBirthUnix: dob.valueOf(),
      };
    }
    return profileObj;
  }

  uploadProfileImg(fileInputEvent: Event) {
    let file: File = (fileInputEvent.target as HTMLInputElement).files[0];

    const pattern = /.jpg|.jpeg|.png/;

    if (!file.type.match(pattern)) {
      this.coreService.showErrorDialog('Selected image file is not supported');
      return;
    }
    const formData = new FormData();
    formData.append('category', UploadFileCategory.ProfilePicture);
    formData.append('file', file);

    this.uploadingImg = true;
    this.profileService
      .uploadProfilePicture(formData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res: UploadFileResponse) => {
          if (!res.fileUrl) {
            const error = new Error('File URL is missing in the response');
            this.coreService.showErrorDialog(error);
            log.error(error);
            this.uploadingImg = false;
            return;
          }
          let uploadProfileImgBody = {
            link: res.fileUrl,
          };
          this.profileService
            .uploadProfileImg(uploadProfileImgBody)
            .pipe(
              finalize(() => (this.uploadingImg = false)),
              takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(
              (res) => {
                this.analytics.trackEvent('profile_updated', {
                  field_updated: 'profile_picture_updated',
                });
                this.imgUrl = res.imageUrl;
                this.profileService.profileImageUpdated$.next(res.imageUrl);
              },
              (err: HttpErrorResponse) => {
                this.coreService.showErrorDialog(err);
                log.error(err);
              },
            );
        },
        (err: HttpErrorResponse) => {
          this.coreService.showErrorDialog(err);
          log.error(err);
        },
      );
  }

  cancelEditEmail() {
    this.enableEditEmail = false;
    this.emailFormGroup.markAsUntouched();
    this.emailFormGroup.get('email').setValue(this.profileDetails.email);
  }

  cancelEditPhone() {
    this.enableEditPhone = false;
    this.phoneFormGroup.markAsUntouched();
    this.phoneFormGroup.get('phone').setValue(this.profileDetails.phone);
  }

  successMsg(msg: string) {
    this.coreService.notify(msg);
  }

  confirmRemoveImg() {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '600px',
      data: {
        title: 'Confirm Photo Deletion',
        description: 'Are you sure you want to delete your profile photo?',
        imageUrl: 'assets/dialogs/delete.png',
        onConfirmation: () => {
          this.removeImg();
        },
        cancel: () => {},
      },
    });
  }

  removeImg() {
    this.profileService
      .removeProfileImg()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.analytics.trackEvent('profile_updated', {
          field_updated: 'profile_picture_removed',
        });
        this.profileService.profileImageUpdated$.next(null);
        this.getProfileDetails();
      });
  }
  mapBlockingCodeToMessageKey(code: string): string {
    return `blocking.${code}`;
  }

  private forceLogout(): void {
    this.credentialsService.setCredentials();
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  private startCoolOffCountdown(): void {
    this.countdownSub?.unsubscribe();
    const req = this.deletionRequest;
    if (!req || req.canConfirmNow || this.isDeletionExpired) return;
    const utcString = req.coolOffUntilUtc.endsWith('Z')
      ? req.coolOffUntilUtc
      : req.coolOffUntilUtc + 'Z';
    const target = new Date(utcString).getTime();
    if (target <= Date.now()) {
      this.coolOffCountdown = '00:00:00';
      return;
    }
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        this.coolOffCountdown = '00:00:00';
        this.countdownSub?.unsubscribe();
        this.getProfileDetails();
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      this.coolOffCountdown = `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
    };
    tick();
    this.countdownSub = interval(1000).subscribe(tick);
  }

  private pad(n: number): string {
    return n.toString().padStart(2, '0');
  }

  openDeactivateConfirmation(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: this.translateService.instant('account.deactivate.title'),
        description: this.translateService.instant(
          'account.deactivate.description',
        ),
        imageUrl: 'assets/dialogs/delete.png',
        confirmBtnText: this.translateService.instant(
          'account.deactivate.action',
        ),
        onConfirmation: () => {
          this.profileService
            .sendOtp({ phoneNumber: this.profileDetails.phone })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.dialog
                  .open(OtpDialogComponent, {
                    width: '420px',
                    data: {
                      mode: 'verify-phone',
                      phoneNumber: this.profileDetails.phone,
                    },
                  })
                  .afterClosed()
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe({
                    next: (verified: boolean) => {
                      if (verified) {
                        this.profileService
                          .deactivateAccount()
                          .pipe(takeUntilDestroyed(this.destroyRef))
                          .subscribe({
                            next: () => {
                              this.coreService.notify(
                                this.translateService.instant(
                                  'account.deactivate.success',
                                ),
                              );
                              this.forceLogout();
                            },
                            error: (err: HttpErrorResponse) =>
                              this.coreService.showErrorDialog(err),
                          });
                      }
                    },
                  });
              },
              error: (err: HttpErrorResponse) =>
                this.coreService.showErrorDialog(err),
            });
        },
      },
    });
  }

  private showBlockedDialog(blockingReasonCodes: string[]): void {
    const reasons = blockingReasonCodes
      .map(
        (code) =>
          `<li>${this.translateService.instant(this.mapBlockingCodeToMessageKey(code))}</li>`,
      )
      .join('');
    const description = `<ul style="text-align:start;padding-left:1.2rem">${reasons}</ul>`;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '520px',
      data: {
        title: this.translateService.instant('account.delete.blockedTitle'),
        description,
        imageUrl: 'assets/dialogs/delete.png',
        confirmBtnText: this.translateService.instant(
          'account.delete.goToBookings',
        ),
        onConfirmation: () => {
          dialogRef.close();
          this.router.navigate(['/bookings']);
        },
        cancel: () => {},
      },
    });
  }

  private openDeleteConfirmation(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '520px',
      data: {
        title: this.translateService.instant('account.delete.title'),
        description: this.translateService.instant(
          'account.delete.irreversibleDescription',
        ),
        imageUrl: 'assets/dialogs/delete.png',
        confirmBtnText: this.translateService.instant(
          'account.delete.continue',
        ),
        onConfirmation: () => {
          this.profileService
            .requestPermanentDelete()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.coreService.notify(
                  this.translateService.instant('account.delete.requested'),
                );
                this.getProfileDetails();
              },
              error: (err: HttpErrorResponse) => {
                const codes: string[] = err?.error?.errors ?? [];
                if (codes.length) {
                  this.showBlockedDialog(codes);
                } else {
                  this.coreService.showErrorDialog(err);
                }
              },
            });
        },
        cancel: () => {},
      },
    });
  }

  confirmDeletion(): void {
    this.profileService
      .sendOtp({ phoneNumber: this.profileDetails.phone })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.dialog
            .open(OtpDialogComponent, {
              width: '420px',
              data: {
                mode: 'delete-account',
                phoneNumber: this.profileDetails.phone,
              },
            })
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (deleted: boolean) => {
                if (deleted) {
                  this.analytics.trackEvent('account_deleted');
                  this.coreService.notify(
                    this.translateService.instant('account.delete.success'),
                  );
                  this.forceLogout();
                }
              },
            });
        },
        error: (err: HttpErrorResponse) =>
          this.coreService.showErrorDialog(err),
      });
  }

  get deletionRequest() {
    return this.profileDetails?.deletionRequest;
  }

  get isDeletionExpired(): boolean {
    return (
      this.deletionRequest?.status === 'Pending' &&
      !!this.deletionRequest.expiresAtUtc &&
      new Date(
        this.deletionRequest.expiresAtUtc.endsWith('Z')
          ? this.deletionRequest.expiresAtUtc
          : this.deletionRequest.expiresAtUtc + 'Z',
      ) < new Date()
    );
  }

  openChangeEmailDialog(): void {
    this.router.navigate(['/profile/update-email']);
  }

  openChangePhoneDialog(): void {
    this.router.navigate(['/profile/update-phone']);
  }

  openChangePasswordDialog(): void {
    this.router.navigate(['/profile/update-password']);
  }

  removeAccount(): void {
    this.profileService
      .getDeleteEligibility()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (eligibility: DeleteEligibility) => {
          if (eligibility.isEligible) {
            this.openDeleteConfirmation();
          } else {
            this.showBlockedDialog(eligibility.blockingReasonCodes ?? []);
          }
        },
        error: (err: HttpErrorResponse) =>
          this.coreService.showErrorDialog(err),
      });
  }
}
